import React from "react";
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Button,
  Divider,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  FileCopy as FileCopyIcon,
  Refresh as RefreshIcon,
  PhoneAndroid as PhoneAndroidIcon,
  AppRegistration as AppRegistrationIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { CopyToClipboard } from "react-copy-to-clipboard";
import ClientData from "../../../../../data/ClientData";

import Mighty45MobileStoreLinks from "../../../../../components/Mighty45MobileStoreLinks";

const StyledCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  height: "100%",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(5),
  padding: theme.spacing(1, 2),
  textTransform: "none",
  fontWeight: 600,
  margin: theme.spacing(0, 0.5),
}));

const CodeDisplay = styled(Typography)(({ theme }) => ({
  fontFamily: "monospace",
  fontWeight: "bold",
  fontSize: "1.2rem",
  padding: theme.spacing(0.75, 1.5),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  marginRight: theme.spacing(1),
}));

const ClientConnectCodeCard = ({ client }) => {
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });

  if (!client) {
    return null;
  }

  const handleCopy = (type) => {
    setSnackbar({
      open: true,
      message: `${type} copied to clipboard!`,
      severity: "success",
    });
  };

  const handleNewCode = () => {
    ClientData.resetConnectCode(client.uid);
    setSnackbar({
      open: true,
      message: "New code generated!",
      severity: "success",
    });
  };

  const onboardingSteps = [
    {
      icon: <SendIcon />,
      title: "Share the connect code with your client",
      description:
        "Send via email/message from your inbox, Mighty45 onboarding email, SMS, etc.",
    },
    {
      icon: <PhoneAndroidIcon />,
      title: "Have them download the Mighty45 mobile app",
      description: "Send them App Store / Google Play links directly from this card.",
    },
    {
      icon: <AppRegistrationIcon />,
      title: "Client opens Mighty45 on their phone/tablet",
      description: "They should tap Register.",
    },
    {
      icon: <PersonAddIcon />,
      title: "Enter the connect code exactly as shown",
      description:
        "The code joins them to your organization after they finish registration.",
    },
    {
      icon: <CheckCircleIcon />,
      title: "Client registration completes",
      description:
        "They will be visible in Mighty45 once they successfully register.",
    },
  ];

  return (
    <>
      <StyledCard>
        <CardHeader
          title="Connect Code"
          subheader="Your client installs the Mighty45 app and enters this during Register."
        />
        <Divider />
        <CardContent>
          <Box sx={{ mb: 3 }}>
            {client.connectCode ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <CodeDisplay variant="body1">{client.connectCode}</CodeDisplay>

                  <CopyToClipboard
                    text={client.connectCode}
                    onCopy={() => handleCopy("Code")}
                  >
                    <IconButton
                      size="small"
                      aria-label="Copy code"
                      disableRipple
                      disableFocusRipple
                    >
                      <FileCopyIcon />
                    </IconButton>
                  </CopyToClipboard>
                </Box>

                <Mighty45MobileStoreLinks sx={{ mb: 2 }} />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Press &ldquo;Get New Code&rdquo; to generate a connect code
              </Typography>
            )}
          </Box>

          {/* Onboarding Steps */}
          {client.connectCode && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                How to Invite Your Client
              </Typography>

              <List sx={{ p: 0 }}>
                {onboardingSteps.map((step, index) => (
                  <ListItem
                    key={`${step.title}-${index}`}
                    sx={{ alignItems: "flex-start", px: 0, py: 1 }}
                  >
                    <ListItemIcon sx={{ mt: 0.5, minWidth: 48 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          backgroundColor: "primary.main",
                          color: "white",
                        }}
                      >
                        {step.icon}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          fontWeight="600"
                          sx={{ mb: 0.5 }}
                        >
                          {index + 1}. {step.title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {step.description}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <ActionButton
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleNewCode}
            >
              Get New Code
            </ActionButton>
          </Box>
        </CardContent>
      </StyledCard>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ClientConnectCodeCard;
